import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import Typography from "@mui/material/Typography"
import CurrencyBitcoin from '@mui/icons-material/CurrencyBitcoin';
import MoreVert from '@mui/icons-material/MoreVert';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded';
import { Summary, SummaryItem } from "../../interface/Summary";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useState } from "react";
import Skeleton from "@mui/material/Skeleton";
import { formatPrice } from "../../utils/formatPrice";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import { dashboardMutedTextSx, getDashboardPanelSx } from "./dashboardStyles";

interface AssetCardProps
{
    summary: Summary | undefined
    summaryItem: SummaryItem | undefined
    loading: boolean
    assetName: string
}



export const AssetCard = ({ loading, summary, summaryItem, assetName }: AssetCardProps) =>
{
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const getChangePercentColor = () =>
  {
    if(summary && summaryItem && Number(summaryItem?.dailyStats.priceChangePercent) > 0) return "success"
    return "error"
  }

  const color = getChangePercentColor()

  return (
    <Card sx={{ ...getDashboardPanelSx(theme, 250), p: 3 }} elevation={0}>
    
    { loading ? <>

      <Skeleton height={30} width={30} variant="circular" /> 
      <Skeleton height={20} width="40%" sx={{ mt: 1 }} /> 
      <Skeleton height={42} width="70%" /> 
      <Skeleton height={96} width="100%" sx={{ mt: 2 }} /> 

    </>
    : <>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ position: "relative", zIndex: 1 }}>
      <Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            color: "#f7b955",
            backgroundColor: alpha("#f7b955", theme.palette.mode === "dark" ? 0.14 : 0.2),
            mb: 1.5,
          }}
        >
          <CurrencyBitcoin fontSize="large" />
        </Box>
        <Typography variant="overline" sx={{ letterSpacing: 1.6, ...dashboardMutedTextSx(theme) }}>
          Asset Position
        </Typography>
      </Box>

      <Button
        size="small"
        variant="outlined"
        color={color}
        sx={{
          borderColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.12 : 0.18),
          color: "text.primary",
        }}
        endIcon={<MoreVert />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
          Change Percent: {Number(summaryItem?.dailyStats.priceChangePercent)}%
      </Button>
    </Stack>

    <Typography sx={{ position: "relative", zIndex: 1, mt: 2, fontSize: { xs: 32, md: 40 }, lineHeight: 1, fontWeight: 700 }}>
      {summaryItem?.free}
    </Typography>
    <Typography sx={{ position: "relative", zIndex: 1, mt: 1, fontSize: 16, ...dashboardMutedTextSx(theme) }}>
      {assetName} equivalent ${summaryItem?.estValueInUsd?.toFixed(2)} USDT
    </Typography>

    <Stack
      spacing={1.1}
      sx={{
        position: "relative",
        zIndex: 1,
        mt: 2.5,
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.12)}`,
        backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.55),
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Last Price</Typography>
        <Typography variant="body2">${formatPrice(Number(summaryItem?.dailyStats.lastPrice))}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>Bid</Typography>
        <Typography variant="body2">${formatPrice(Number(summaryItem?.dailyStats.bidPrice))}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>24h Range</Typography>
        <Typography variant="body2">
          ${formatPrice(Number(summaryItem?.dailyStats.lowPrice))} to ${formatPrice(Number(summaryItem?.dailyStats.highPrice))}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {Number(summaryItem?.dailyStats.priceChangePercent) > 0 ? (
          <NorthEastRoundedIcon color="success" fontSize="small" />
        ) : (
          <SouthEastRoundedIcon color="error" fontSize="small" />
        )}
        <Typography variant="body2" sx={dashboardMutedTextSx(theme)}>
          {Number(summaryItem?.dailyStats.priceChangePercent) > 0 ? "Momentum is positive today" : "Momentum is soft today"}
        </Typography>
      </Stack>
    </Stack>

     <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)} >
      <MenuItem onClick={() => setAnchorEl(null)}>Ask Price: ${formatPrice(Number(summaryItem?.dailyStats.askPrice))}</MenuItem>
      <MenuItem onClick={() => setAnchorEl(null)}>Price Change: ${formatPrice(Number(summaryItem?.dailyStats.priceChange))}</MenuItem>
      </Menu>

    </>}
  </Card>
  )
}
